import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import {
  claimConversation,
  lockConflictDetailSchema,
  type Lock,
  type LockConflictDetail,
} from '@/features/conversations/conversations.api';
import { conversationsKeys } from '@/features/conversations/keys';
import { useAuthStore } from '@/store/auth';

interface ClaimVariables {
  conversationId: string;
  force?: boolean;
}

interface ClaimMutationContext {
  /** The lock cache value before our optimistic write, so `onError` can roll
   *  back to exactly what was there (not "no lock", which would be wrong if
   *  the conversation was already self-locked when the mutation fired). */
  previousLock: Lock | undefined;
}

/**
 * Claim (or force-claim) a conversation's lock.
 *
 *   - onMutate: writes an optimistic Lock owned by the current agent into
 *     `['conversations', 'lock', id]` so the row immediately shows the green
 *     self-lock pill. Captures the prior cache value for rollback.
 *   - onError: rolls back the optimistic write. On a 409 we parse the
 *     conflict body and toast "Already locked by {display_name}"; on any
 *     other error we toast a generic try-again message.
 *   - onSuccess: writes the server-side Lock into the same cache slot,
 *     overwriting the optimistic shape with authoritative timestamps.
 *   - onSettled: invalidates the lock cache slot so the next render is
 *     fed by a fresh server fetch (defensive; the success path already
 *     wrote real data).
 */
export function useClaimConversation() {
  const queryClient = useQueryClient();
  const agent = useAuthStore((s) => s.agent);

  return useMutation<Lock, unknown, ClaimVariables, ClaimMutationContext>({
    mutationFn: ({ conversationId, force }) =>
      claimConversation(conversationId, { force }),

    onMutate: async ({ conversationId }) => {
      if (!agent) {
        // Defensive — the UI never surfaces Claim without a session, but if
        // it somehow does, surface a quiet toast and bail so we don't write
        // a malformed lock into the cache.
        toast('Not signed in');
        throw new Error('not authenticated');
      }

      const lockKey = conversationsKeys.lock(conversationId);

      await queryClient.cancelQueries({ queryKey: lockKey });
      const previousLock = queryClient.getQueryData<Lock>(lockKey);

      const now = new Date();
      // Backend TTL is server-controlled; use 60s here for the optimistic
      // copy. The server response overwrites this before it matters.
      const expires = new Date(now.getTime() + 60_000);
      queryClient.setQueryData<Lock>(lockKey, {
        conversation_id: conversationId,
        locked_by_agent_id: agent.id,
        locked_by_display_name: agent.display_name,
        locked_at: now.toISOString(),
        expires_at: expires.toISOString(),
      });

      return { previousLock };
    },

    onError: (error, { conversationId }, context) => {
      const lockKey = conversationsKeys.lock(conversationId);
      // Roll back to exactly what was in the cache before — including
      // explicit `undefined` (no lock). `setQueryData(key, undefined)` is a
      // no-op in react-query (the cache slot keeps its prior value), so for
      // the "no prior lock" rollback we use `removeQueries`.
      if (context?.previousLock === undefined) {
        queryClient.removeQueries({ queryKey: lockKey, exact: true });
      } else {
        queryClient.setQueryData(lockKey, context.previousLock);
      }

      if (isAxiosError(error) && error.response?.status === 409) {
        const detail = parseConflictDetail(error.response.data);
        if (detail) {
          toast(`Already locked by ${detail.locked_by_display_name}`);
          // Seed the lock cache with the conflict body so the row instantly
          // shows who actually owns it — saves a follow-up fetch.
          queryClient.setQueryData<Lock>(lockKey, {
            conversation_id: detail.conversation_id,
            locked_by_agent_id: detail.locked_by_agent_id,
            locked_by_display_name: detail.locked_by_display_name,
            locked_at: detail.locked_at,
            expires_at: detail.expires_at,
          });
          return;
        }
      }
      toast("Couldn't claim — try again");
    },

    onSuccess: (lock, { conversationId }) => {
      queryClient.setQueryData<Lock>(
        conversationsKeys.lock(conversationId),
        lock,
      );
    },

    onSettled: (_data, _error, { conversationId }) => {
      // Defensive: keep the cache slot consistent with the server even if a
      // race elsewhere mutated it mid-flight.
      void queryClient.invalidateQueries({
        queryKey: conversationsKeys.lock(conversationId),
      });
    },
  });
}

function parseConflictDetail(data: unknown): LockConflictDetail | null {
  // FastAPI wraps `HTTPException(detail=...)` payloads in `{ detail: ... }`.
  if (typeof data !== 'object' || data === null) return null;
  const maybeDetail = (data as { detail?: unknown }).detail ?? data;
  const result = lockConflictDetailSchema.safeParse(maybeDetail);
  return result.success ? result.data : null;
}
